/**
 * Lens Family Graph
 *
 * Controls for the Lens ecosystem (Civitai-internal, comfy engine).
 * Meta contains only dynamic props - static props defined in components.
 *
 * Two models discriminated by lensVariant (computed from model.id):
 * - base: full-step variant
 * - turbo: low-step variant
 *
 * Both variants support LoRAs and the same resolution/aspect-ratio choices.
 * Sampler (euler) and scheduler (simple) are fixed — set in the handler, not exposed in UI.
 */

import z from 'zod';
import { DataGraph } from '~/libs/data-graph/data-graph';
import type { GenerationCtx } from './context';
import type { AspectRatioOption, ResourceData } from './common';
import {
  aspectRatioNode,
  createCheckpointGraph,
  negativePromptGraph,
  promptGraph,
  resourcesNode,
  seedNode,
  sliderNode,
  snippetsGraph,
  triggerWordsGraph,
} from './common';

// =============================================================================
// Constants
// =============================================================================

/** Lens version IDs */
export const lensVersionIds = {
  base: 2982236,
  turbo: 2982241,
} as const;

type LensVariant = 'base' | 'turbo';

/** Options for lens version selector (using version IDs as values) */
const lensVersionOptions = [
  { label: 'Base', value: lensVersionIds.base },
  { label: 'Turbo', value: lensVersionIds.turbo },
];

/** Map version ID to variant */
const versionIdToVariant = new Map<number, LensVariant>([
  [lensVersionIds.base, 'base'],
  [lensVersionIds.turbo, 'turbo'],
]);

// =============================================================================
// Resolution + Aspect Ratios
// =============================================================================

/** Per the model card, Lens accepts a base resolution of 1024 or 1440. */
const lensResolutionOptions = ['1024', '1440'] as const;

/**
 * Aspect ratios per base resolution. Each set is area-matched to its base
 * (1024² ≈ 1.05M pixels, 1440² ≈ 2.07M pixels) and rounded to multiples of 16
 * so the orchestrator's diffusion model gets clean dimensions either way.
 */
const lensAspectRatiosByResolution: Record<string, AspectRatioOption[]> = {
  '1024': [
    { label: '1:2', value: '1:2', width: 720, height: 1440 },
    { label: '9:16', value: '9:16', width: 768, height: 1376 },
    { label: '2:3', value: '2:3', width: 832, height: 1248 },
    { label: '3:4', value: '3:4', width: 880, height: 1184 },
    { label: '1:1', value: '1:1', width: 1024, height: 1024 },
    { label: '4:3', value: '4:3', width: 1184, height: 880 },
    { label: '3:2', value: '3:2', width: 1248, height: 832 },
    { label: '16:9', value: '16:9', width: 1376, height: 768 },
    { label: '2:1', value: '2:1', width: 1440, height: 720 },
  ],
  '1440': [
    { label: '1:2', value: '1:2', width: 1024, height: 2032 },
    { label: '9:16', value: '9:16', width: 1088, height: 1920 },
    { label: '2:3', value: '2:3', width: 1184, height: 1760 },
    { label: '3:4', value: '3:4', width: 1248, height: 1664 },
    { label: '1:1', value: '1:1', width: 1440, height: 1440 },
    { label: '4:3', value: '4:3', width: 1664, height: 1248 },
    { label: '3:2', value: '3:2', width: 1760, height: 1184 },
    { label: '16:9', value: '16:9', width: 1920, height: 1088 },
    { label: '2:1', value: '2:1', width: 2032, height: 1024 },
  ],
};

/** The most common aspect ratios — shown before the "More" overflow in the UI. */
const lensPriorityRatios = ['16:9', '4:3', '1:1', '3:4', '9:16'];

// =============================================================================
// Variant Subgraphs
// =============================================================================

/** Base model: cfg 5.0, steps 20 (model card defaults), LoRA support */
const baseGraph = new DataGraph<{ ecosystem: string }, GenerationCtx>()
  .node(
    'resources',
    (ctx, ext) =>
      resourcesNode({
        ecosystem: ctx.ecosystem,
        limit: ext.limits.maxResources,
      }),
    ['ecosystem']
  )
  .node('cfgScale', sliderNode({ min: 1, max: 20, defaultValue: 5, step: 0.5 }))
  .node('steps', sliderNode({ min: 1, max: 50, defaultValue: 20 }));

/** Turbo model: tight cfg range (1-2), low step count, LoRA support */
const turboGraph = new DataGraph<{ ecosystem: string }, GenerationCtx>()
  .node(
    'resources',
    (ctx, ext) =>
      resourcesNode({
        ecosystem: ctx.ecosystem,
        limit: ext.limits.maxResources,
      }),
    ['ecosystem']
  )
  .node('cfgScale', sliderNode({ min: 1, max: 2, step: 0.1, defaultValue: 1 }))
  .node('steps', sliderNode({ min: 1, max: 12, defaultValue: 4 }));

// =============================================================================
// Lens Graph
// =============================================================================

/**
 * Lens family controls.
 *
 * Discriminates on lensVariant (computed from model.id) to select base vs turbo subgraph.
 * Resolution is a parent-level node so it stays available across variants and aspectRatio
 * recomputes its option set when resolution changes.
 */
export const lensGraph = new DataGraph<
  { ecosystem: string; workflow: string; model: ResourceData },
  GenerationCtx
>()
  .merge(
    () =>
      createCheckpointGraph({
        versions: { options: lensVersionOptions },
        defaultModelId: lensVersionIds.base,
      }),
    []
  )
  .computed(
    'lensVariant',
    (ctx) => (ctx.model?.id ? versionIdToVariant.get(ctx.model.id) : undefined) ?? 'base',
    ['model']
  )
  .discriminator('lensVariant', {
    base: baseGraph,
    turbo: turboGraph,
  })
  .node('resolution', {
    input: z.enum(lensResolutionOptions).optional(),
    output: z.enum(lensResolutionOptions),
    defaultValue: '1024',
    meta: {
      options: lensResolutionOptions.map((r) => ({ label: r, value: r })),
    },
  })
  .node(
    'aspectRatio',
    (ctx) => {
      const resolution = (ctx as { resolution?: string }).resolution ?? '1024';
      return aspectRatioNode({
        options: lensAspectRatiosByResolution[resolution],
        defaultValue: '1:1',
        priorityOptions: lensPriorityRatios,
      });
    },
    ['resolution']
  )
  .merge(triggerWordsGraph)
  .merge(snippetsGraph)
  .merge(promptGraph)
  .merge(negativePromptGraph)
  .node('seed', seedNode());
